import { Button } from "@/components/ui/button"
import {
    Card,
    CardContent,
    CardDescription,
    CardFooter,
    CardHeader,
    CardTitle,
} from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Link, useNavigate } from "react-router-dom"
import { useState, ChangeEvent } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { useRequestAccountOwnershipTransfer } from "@/api/userQueries"
import toast from "react-hot-toast"
import { Loader2 } from "lucide-react"

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export const AccountTransferOwnership = () => {
    const navFn = useNavigate()
    const queryClient = useQueryClient()
    const [firstName, setFirstName] = useState("")
    const [lastName, setLastName] = useState("")
    const [email, setEmail] = useState("")
    const [firstNameError, setFirstNameError] = useState("")
    const [lastNameError, setLastNameError] = useState("")
    const [emailError, setEmailError] = useState("")

    const handleFirstNameChange = (e: ChangeEvent<HTMLInputElement>) => {
        setFirstName(e.target.value)
        if (firstNameError) setFirstNameError("")
    }
    const handleLastNameChange = (e: ChangeEvent<HTMLInputElement>) => {
        setLastName(e.target.value)
        if (lastNameError) setLastNameError("")
    }
    const handleEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
        setEmail(e.target.value)
        if (emailError) setEmailError("")
    }

    const requestTransferOwnershipMutation =
        useRequestAccountOwnershipTransfer()

    const handleRequestOwnershipTransfer = async () => {
        let hasErrors = false
        if (!firstName.trim()) {
            setFirstNameError("First name is required")
            hasErrors = true
        } else {
            setFirstNameError("")
        }
        if (!lastName.trim()) {
            setLastNameError("Last name is required")
            hasErrors = true
        } else {
            setLastNameError("")
        }
        if (!email.trim()) {
            setEmailError("Email is required")
            hasErrors = true
        } else if (!emailRegex.test(email)) {
            setEmailError("Invalid email format")
            hasErrors = true
        } else {
            setEmailError("")
        }
        if (hasErrors) return
        if (requestTransferOwnershipMutation.isPending) return
        try {
            await requestTransferOwnershipMutation.mutateAsync({
                firstName,
                lastName,
                email,
            })
            queryClient.invalidateQueries({ queryKey: ["userDetails"] })
            toast.success("Successfully requested account ownership transfer.")
            await queryClient.invalidateQueries({ queryKey: ["userDetails"] })
            await queryClient.refetchQueries({ queryKey: ["userDetails"] })
            navFn("/account")
        } catch (error: unknown) {
            if (error instanceof Error) {
                console.log(error)
                toast.error("Unable to request account ownership transfer.")
            }
        }
    }

    return (
        <div className="flex min-h-screen w-full flex-col pb-20 pt-8 bg-muted/40">
            <main className="flex min-h-[calc(100vh_-_theme(spacing.16))] flex-1 flex-col gap-4 p-4 md:gap-8 md:p-10">
                <div className="mx-auto grid w-full max-w-xl items-start gap-6">
                    <Button
                        asChild={true}
                        variant="outline"
                        className="justify-self-start"
                    >
                        <Link to={".."} relative="path">
                            Back
                        </Link>
                    </Button>
                    <Card>
                        <CardHeader>
                            <CardTitle>Transfer Ownership</CardTitle>
                            <CardDescription>
                                Enter the first name, last name, email address,
                                and your current password to transfer ownership
                                to the new person.
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="first-name">
                                    New Owner's First Name
                                </Label>
                                <Input
                                    id="first-name"
                                    value={firstName}
                                    onChange={handleFirstNameChange}
                                    placeholder="First name"
                                    className={
                                        firstNameError
                                            ? "border-destructive focus-visible:ring-destructive"
                                            : ""
                                    }
                                />
                                {firstNameError && (
                                    <p className="text-xs text-destructive mt-1">
                                        {firstNameError}
                                    </p>
                                )}
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="last-name">
                                    New Owner's Last Name
                                </Label>
                                <Input
                                    id="last-name"
                                    value={lastName}
                                    onChange={handleLastNameChange}
                                    placeholder="Last name"
                                    className={
                                        lastNameError
                                            ? "border-destructive focus-visible:ring-destructive"
                                            : ""
                                    }
                                />
                                {lastNameError && (
                                    <p className="text-xs text-destructive mt-1">
                                        {lastNameError}
                                    </p>
                                )}
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="email">
                                    New Owner's Email Address
                                </Label>
                                <Input
                                    id="email"
                                    type="email"
                                    value={email}
                                    onChange={handleEmailChange}
                                    placeholder="Email address"
                                    className={
                                        emailError
                                            ? "border-destructive focus-visible:ring-destructive"
                                            : ""
                                    }
                                />
                                {emailError && (
                                    <p className="text-xs text-destructive mt-1">
                                        {emailError}
                                    </p>
                                )}
                            </div>
                        </CardContent>
                        <CardFooter>
                            <Button
                                className="w-full"
                                onClick={handleRequestOwnershipTransfer}
                            >
                                {requestTransferOwnershipMutation.isPending ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Sending request...
                                    </>
                                ) : (
                                    "Request Account Ownership Transfer"
                                )}
                            </Button>
                        </CardFooter>
                    </Card>
                </div>
            </main>
        </div>
    )
}
